import React, { useEffect, useState } from 'react';
import type { Edge, OnSelectionChangeParams } from '@xyflow/react';
import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';

import EmptyState from '~/components/core/EmptyState';
import { EmptyReportsIllustration } from '~/components/illustrations';
import LoadingIndicator from '~/components/LoadingIndicator';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type { ErdSchemaResponse } from '~/server_shared/openapi/data-contracts';

import type { TableNodeType } from './erdGraph';
import { buildGraph, getTableNodeHeight, layoutGraph, TABLE_NODE_WIDTH } from './erdGraph';
import { TableNode } from './TableNode';

import '@xyflow/react/dist/style.css';

const nodeTypes = { table: TableNode };

const ErdFlow: React.FC<{ schema: ErdSchemaResponse }> = ({ schema }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<TableNodeType>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [search, setSearch] = useState('');
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const { fitView, setCenter } = useReactFlow();

  useEffect(() => {
    let active = true;
    const { nodes: builtNodes, edges: builtEdges } = buildGraph(schema);
    layoutGraph(builtNodes, builtEdges).then((layoutedNodes) => {
      if (!active) return;
      setNodes(layoutedNodes);
      setEdges(builtEdges);
      window.requestAnimationFrame(() => fitView());
    });
    return () => {
      active = false;
    };
  }, [schema, setNodes, setEdges, fitView]);

  // Clicking a table highlights it plus every directly related table and FK edge, dimming the
  // rest of the graph; clicking the canvas (or another table) resets. Selection state itself is
  // ReactFlow's — this only derives the neighborhood styling from it.
  const connectedEdgeIds = new Set<string>();
  const connectedTableIds = new Set<string>();
  if (selectedTableId) {
    edges.forEach((edge) => {
      if (edge.source === selectedTableId || edge.target === selectedTableId) {
        connectedEdgeIds.add(edge.id);
        connectedTableIds.add(edge.source === selectedTableId ? edge.target : edge.source);
      }
    });
  }

  const displayNodes = selectedTableId
    ? nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isConnected: connectedTableIds.has(node.id),
          isDimmed: node.id !== selectedTableId && !connectedTableIds.has(node.id),
        },
      }))
    : nodes;

  const displayEdges = selectedTableId
    ? edges.map((edge) =>
        connectedEdgeIds.has(edge.id)
          ? { ...edge, animated: true, style: { stroke: '#0d9488', strokeWidth: 2 } }
          : { ...edge, style: { opacity: 0.15 } }
      )
    : edges;

  const onSelectionChange = ({ nodes: selectedNodes }: OnSelectionChangeParams) => {
    setSelectedTableId(selectedNodes.length === 1 ? selectedNodes[0].id : null);
  };

  const jumpToTable = (tableName: string) => {
    const query = tableName.trim().toLowerCase();
    if (!query) return;
    const match =
      nodes.find((node) => node.id.toLowerCase() === query) ??
      nodes.find((node) => node.id.toLowerCase().startsWith(query)) ??
      nodes.find((node) => node.id.toLowerCase().includes(query));
    if (!match) return;
    setCenter(
      match.position.x + TABLE_NODE_WIDTH / 2,
      match.position.y + getTableNodeHeight(match.data.columns.length) / 2,
      { zoom: 0.85, duration: 500 }
    );
  };

  return (
    <ReactFlow
      nodes={displayNodes}
      edges={displayEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onSelectionChange={onSelectionChange}
      nodeTypes={nodeTypes}
      nodesConnectable={false}
      minZoom={0.02}
      fitView
    >
      <Background />
      <Controls />
      <MiniMap pannable zoomable />
      <Panel position="top-left">
        <form
          className="shadow rounded-md bg-white p-2"
          onSubmit={(event) => {
            event.preventDefault();
            jumpToTable(search);
          }}
        >
          <input
            className="w-64 py-1 rounded border border-slate-300 px-2 text-sm"
            placeholder="Search table and press Enter"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </form>
      </Panel>
    </ReactFlow>
  );
};

// The sysconfig tab layout has no constrained-height ancestor, so the ReactFlow canvas (which
// requires an explicitly sized container) gets a viewport-based height.
const CONTAINER_HEIGHT_CLASS = 'h-[calc(100vh-380px)] min-h-[400px]';

export const ErdViewerPage: React.FC = () => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const orgId = organization?.id;

  const {
    isLoading,
    isError,
    httpStatus,
    data: schema,
  } = useDataFetcher<ErdSchemaResponse>(`/api/v1/metabase/organizations/${orgId}/erd_schema`, null, {
    shouldFetch: !!orgId,
    // The error states are rendered inline below, so suppress the global error dialog/report.
    shouldReportAxiosError: false,
  });

  // Keep the loading indicator until the organization id is available; otherwise the fetch is
  // skipped (shouldFetch is false) and we'd flash the error state before the org has loaded.
  if (!orgId || isLoading) {
    return (
      <div className={`flex items-center justify-center ${CONTAINER_HEIGHT_CLASS}`}>
        <LoadingIndicator />
      </div>
    );
  }

  if (isError || !schema) {
    // A 403 means Metabase isn't enabled for this org; any other failure is a genuine error
    // (backend/sink-DB outage, network) and must not be mislabelled as a config problem.
    const isNotConfigured = httpStatus === 403;
    return (
      <EmptyState
        title="Unable to load the data model"
        subtitle={
          isNotConfigured
            ? 'Metabase is not configured for this organization.'
            : 'Something went wrong while loading the data model. Please try again later.'
        }
        illustration={<EmptyReportsIllustration />}
        className={`flex flex-col items-center justify-center ${CONTAINER_HEIGHT_CLASS}`}
      />
    );
  }

  return (
    <div className={`w-full ${CONTAINER_HEIGHT_CLASS}`}>
      <ReactFlowProvider>
        <ErdFlow schema={schema} />
      </ReactFlowProvider>
    </div>
  );
};
